import React, { useState, useEffect } from 'react';
import './AdminDashboard.css';
import { authFetch } from '../utils/api';
import useHeartbeat from '../hooks/useHeartbeat';
import StrategyHealthPanel from './admin/StrategyHealthPanel';
import HealthCheckPanel from './admin/HealthCheckPanel';
import OvernightDigestPanel from './admin/OvernightDigestPanel';
import SystemStatusPanel from './admin/SystemStatusPanel';
import ChecklistsPanel from './admin/ChecklistsPanel';
import ReplayPanel from './admin/ReplayPanel';

function EyeIcon({ visible }) {
  return visible ? (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.97 9.97 0 012.347-3.988m3.17-2.687A9.956 9.956 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.97 9.97 0 01-1.302 2.563M15 12a3 3 0 11-4.243-4.243M15 12l-3-3m6.364 6.364L4.636 4.636" />
    </svg>
  );
}

const ROLES = ['admin', 'officer'];
const SESSIONS = ['AM', 'PM', 'BOTH'];
const DEPARTMENTS = ['Trading', 'Research', 'Operations', 'Compliance', 'Technology', 'Management'];
const ADMIN_TABS = [
  { id: 'users', label: 'User Management', color: '#2962ff' },
  { id: 'health', label: 'Strategy Health', color: '#089981' },
  { id: 'healthcheck', label: 'Health Check', color: '#00897b' },
  { id: 'system', label: 'System Status', color: '#e09b00' },
  { id: 'digest', label: 'Overnight Digest', color: '#7c3aed' },
  { id: 'checklists', label: 'Checklists', color: '#d4495a' },
  { id: 'replay', label: 'Replay (Backtest)', color: '#16a34a' },
];

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetForm, setResetForm] = useState({ new_password: '', confirm_password: '' });
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: '', email: '', phone_number: '', session: '', department: '', role: 'officer' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  useHeartbeat();

  const [currentUser, setCurrentUser] = useState(
    JSON.parse(localStorage.getItem('user') || '{}')
  );

  // Tab nav — Users (existing), Strategy Health, Overnight Digest.
  const [activeTab, setActiveTab] = useState('users');

  const emptyForm = {
    full_name: '', username: '', email: '', phone_number: '',
    password: '', confirm_password: '', role: 'officer', session: 'AM', department: 'Trading',
  };
  const [form, setForm] = useState(emptyForm);
  const [showFormPwd, setShowFormPwd] = useState(false);
  const [showFormConfirm, setShowFormConfirm] = useState(false);
  const [showProfileNewPwd, setShowProfileNewPwd] = useState(false);
  const [showProfileConfirm, setShowProfileConfirm] = useState(false);

  const [profileForm, setProfileForm] = useState({
    full_name: currentUser.full_name || '',
    email: currentUser.email || '',
    phone_number: '',
    session: '',
    department: '',
    new_password: '',
    confirm_password: '',
  });

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch('/authentication/users/activity/');
      const data = await res.json();
      if (data.status === 'success') {
        setUsers(data.users);
      } else {
        setError(data.message || 'Failed to load users');
      }
    } catch {
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // Auto-refresh every 30 seconds to keep online status current
    const interval = setInterval(fetchUsers, 30 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleFormChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleProfileChange = (e) => setProfileForm({ ...profileForm, [e.target.name]: e.target.value });

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setFormError(''); setFormSuccess('');
    if (form.password !== form.confirm_password) { setFormError('Passwords do not match'); return; }
    if (form.password.length < 8) { setFormError('Password must be at least 8 characters'); return; }
    setFormLoading(true);
    try {
      const res = await authFetch('/authentication/officer/signup/', {
        method: 'POST',
        body: JSON.stringify({
          full_name: form.full_name, username: form.username, email: form.email,
          phone_number: form.phone_number, password: form.password,
          confirm_password: form.confirm_password, role: form.role,
          session: form.session, department: form.department,
        }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setFormSuccess(`User "${form.username}" created successfully`);
        setForm(emptyForm);
        fetchUsers();
        setTimeout(() => { setShowForm(false); setFormSuccess(''); }, 2000);
      } else {
        setFormError(data.message || 'Failed to create user');
      }
    } catch { setFormError('Unable to connect to server'); }
    finally { setFormLoading(false); }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setProfileError(''); setProfileSuccess('');
    if (profileForm.new_password && profileForm.new_password !== profileForm.confirm_password) {
      setProfileError('Passwords do not match'); return;
    }
    setProfileLoading(true);
    try {
      const res = await authFetch('/authentication/profile/update/', {
        method: 'POST',
        body: JSON.stringify(profileForm),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setProfileSuccess('Profile updated successfully');
        const updated = { ...currentUser, ...data.user };
        localStorage.setItem('user', JSON.stringify(updated));
        setCurrentUser(updated);
        setTimeout(() => { setShowProfile(false); setProfileSuccess(''); }, 2000);
      } else {
        setProfileError(data.message || 'Failed to update profile');
      }
    } catch { setProfileError('Unable to connect to server'); }
    finally { setProfileLoading(false); }
  };

  const openProfile = () => {
    setProfileForm({
      full_name: currentUser.full_name || '',
      email: currentUser.email || '',
      phone_number: '', session: '', department: '',
      new_password: '', confirm_password: '',
    });
    setProfileError(''); setProfileSuccess('');
    setShowProfile(true);
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      const res = await authFetch(`/authentication/users/${userId}/deactivate/`, {
        method: 'POST',
        body: JSON.stringify({ is_active: !currentStatus }),
      });
      const data = await res.json();
      if (data.status === 'success') { fetchUsers(); }
      else { alert(data.message || 'Action failed'); }
    } catch { alert('Unable to connect to server'); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError(''); setResetSuccess('');
    if (resetForm.new_password !== resetForm.confirm_password) {
      setResetError('Passwords do not match'); return;
    }
    setResetLoading(true);
    try {
      const res = await authFetch(`/authentication/users/${resetTarget.id}/reset-password/`, {
        method: 'POST',
        body: JSON.stringify(resetForm),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setResetSuccess('Password reset successfully');
        setTimeout(() => { setResetTarget(null); setResetSuccess(''); setResetForm({ new_password: '', confirm_password: '' }); }, 2000);
      } else {
        setResetError(data.message || 'Failed to reset password');
      }
    } catch { setResetError('Unable to connect to server'); }
    finally { setResetLoading(false); }
  };

  const handleDelete = async (userId) => {
    try {
      const res = await authFetch(`/authentication/users/${userId}/delete/`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (data.status === 'success') { setDeleteConfirm(null); fetchUsers(); }
      else { alert(data.message || 'Delete failed'); }
    } catch { alert('Unable to connect to server'); }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setEditError(''); setEditSuccess('');
    setEditLoading(true);
    try {
      const res = await authFetch(`/authentication/users/${editTarget.id}/edit/`, {
        method: 'POST',
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setEditSuccess('User updated successfully');
        fetchUsers();
        setTimeout(() => { setEditTarget(null); setEditSuccess(''); }, 2000);
      } else {
        setEditError(data.message || 'Failed to update user');
      }
    } catch { setEditError('Unable to connect to server'); }
    finally { setEditLoading(false); }
  };

  const openEditUser = (user) => {
    setEditForm({
      full_name: user.full_name || '',
      email: user.email || '',
      phone_number: user.phone_number || '',
      session: user.session || '',
      department: user.department || '',
      role: user.role || 'officer',
    });
    setEditError(''); setEditSuccess('');
    setEditTarget(user);
  };

  const handleLogout = async () => {
    try { await authFetch('/authentication/logout/', { method: 'POST' }); }
    catch {}
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  return (
    <div className="admin-wrapper">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <span className="admin-logo">Imole</span>
          <span className="admin-header-sub">Admin Panel</span>
        </div>
        <div className="admin-header-right">
          <a href="/" className="admin-dashboard-link">Main Dashboard</a>
          <button type="button" className="admin-profile-btn" onClick={openProfile}>
            <span className="admin-avatar">{(currentUser.full_name || currentUser.username || 'A')[0].toUpperCase()}</span>
            <span className="admin-welcome-name">{currentUser.full_name || currentUser.username}</span>
          </button>
          <button type="button" className="admin-logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </header>

      <div className="admin-content">
        {/* Tab navigation */}
        <div className="admin-tabs">
          {ADMIN_TABS.map(t => (
            <button
              type="button"
              key={t.id}
              className={`admin-tab ${activeTab === t.id ? 'active' : ''}`}
              style={{
                color: activeTab === t.id ? t.color : undefined,
                borderBottomColor: activeTab === t.id ? t.color : undefined,
              }}
              onClick={() => setActiveTab(t.id)}
            >
              <span
                className="admin-tab-dot"
                style={{ background: activeTab === t.id ? t.color : 'rgba(120,123,134,0.3)' }}
              />
              <span>{t.label}</span>
            </button>
          ))}
        </div>

        {activeTab === 'health'  && <StrategyHealthPanel />}
        {activeTab === 'healthcheck' && <HealthCheckPanel />}
        {activeTab === 'system'  && <SystemStatusPanel />}
        {activeTab === 'digest'  && <OvernightDigestPanel />}
        {activeTab === 'checklists' && <ChecklistsPanel />}
        {activeTab === 'replay' && <ReplayPanel />}

        {activeTab === 'users' && (
        <>
        {/* Page title row */}
        <div className="admin-title-row">
          <div>
            <h1 className="admin-title">User Management</h1>
            <p className="admin-subtitle">Manage all Imole platform users</p>
          </div>
          <button type="button" className="admin-add-btn" onClick={() => { setShowForm(true); setFormError(''); setFormSuccess(''); setForm(emptyForm); }}>
            + Add User
          </button>
        </div>

        {/* Stats */}
        <div className="admin-stats">
          <div className="admin-stat-card">
            <div className="admin-stat-number">{users.length}</div>
            <div className="admin-stat-label">Total Users</div>
          </div>
          <div className="admin-stat-card online">
            <div className="admin-stat-number green">{users.filter(u => u.activity_status === 'online').length}</div>
            <div className="admin-stat-label">Online Now</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-number">{users.filter(u => u.role === 'admin').length}</div>
            <div className="admin-stat-label">Admins</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-number">{users.filter(u => u.role === 'officer').length}</div>
            <div className="admin-stat-label">Officers</div>
          </div>
        </div>

        {error && <div className="admin-error">{error}</div>}

        {/* Users Table */}
        <div className="admin-table-card">
          {loading ? (
            <div className="admin-loading">Loading users...</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Online Status</th>
                  <th>Last Seen</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr><td colSpan={6} className="admin-empty">No users found</td></tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.full_name}</td>
                      <td className="admin-username">@{user.username}</td>
                      <td><span className={`admin-role-badge ${user.role}`}>{user.role}</span></td>
                      <td>
                        <span className={`admin-online-badge ${user.activity_status || 'never'}`}>
                          <span className="admin-online-dot" />
                          {user.activity_status === 'online' ? 'Online' :
                           user.activity_status === 'recently_active' ? 'Recently Active' :
                           user.activity_status === 'today' ? 'Active Today' :
                           user.activity_status === 'inactive' ? 'Offline' : 'Never Logged In'}
                        </span>
                      </td>
                      <td className="admin-date">
                        {user.last_activity
                          ? new Date(user.last_activity).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' })
                          : user.last_login
                            ? new Date(user.last_login).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' })
                            : 'Never'}
                      </td>
                      <td className="admin-actions">
                        <button type="button" className="admin-action-btn edit" onClick={() => openEditUser(user)}>Edit</button>
                        <button
                          type="button"
                          className="admin-action-btn reset"
                          onClick={() => { setResetTarget(user); setResetError(''); setResetSuccess(''); setResetForm({ new_password: '', confirm_password: '' }); setShowResetPwd(false); setShowResetConfirm(false); }}
                        >
                          Reset Password
                        </button>
                        {user.id !== currentUser.id && user.role !== 'admin' && (
                          <>
                            <button type="button" className={`admin-action-btn ${user.is_active ? 'deactivate' : 'activate'}`} onClick={() => handleToggleActive(user.id, user.is_active)}>
                              {user.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button type="button" className="admin-action-btn delete" onClick={() => setDeleteConfirm(user)}>Delete</button>
                          </>
                        )}
                        {user.id === currentUser.id && (
                          <span className="admin-you-badge">You</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
        </>
        )}
      </div>

      {/* Create User Modal */}
      {showForm && (
        <div className="admin-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Add New User</h2>
              <button type="button" className="admin-modal-close" onClick={() => setShowForm(false)}>×</button>
            </div>
            {formError && <div className="admin-form-error">{formError}</div>}
            {formSuccess && <div className="admin-form-success">{formSuccess}</div>}
            <form onSubmit={handleCreateUser} className="admin-form">
              <div className="admin-form-row">
                <div className="admin-form-field">
                  <label>Full Name</label>
                  <input name="full_name" value={form.full_name} onChange={handleFormChange} placeholder="John Doe" required />
                </div>
                <div className="admin-form-field">
                  <label>Username</label>
                  <input name="username" value={form.username} onChange={handleFormChange} placeholder="johndoe" required />
                </div>
              </div>
              <div className="admin-form-row">
                <div className="admin-form-field">
                  <label>Email</label>
                  <input type="email" name="email" value={form.email} onChange={handleFormChange} placeholder="john@pagecapitalng.com" required />
                </div>
                <div className="admin-form-field">
                  <label>Phone Number</label>
                  <input name="phone_number" value={form.phone_number} onChange={handleFormChange} placeholder="+2348012345678" />
                </div>
              </div>
              <div className="admin-form-row">
                <div className="admin-form-field">
                  <label>Password</label>
                  <div className="admin-input-wrap">
                    <input type={showFormPwd ? 'text' : 'password'} name="password" value={form.password} onChange={handleFormChange} placeholder="Min 8 characters" required />
                    <button type="button" className="admin-eye-btn" onClick={() => setShowFormPwd(!showFormPwd)} tabIndex={-1}><EyeIcon visible={showFormPwd} /></button>
                  </div>
                </div>
                <div className="admin-form-field">
                  <label>Confirm Password</label>
                  <div className="admin-input-wrap">
                    <input type={showFormConfirm ? 'text' : 'password'} name="confirm_password" value={form.confirm_password} onChange={handleFormChange} placeholder="Repeat password" required />
                    <button type="button" className="admin-eye-btn" onClick={() => setShowFormConfirm(!showFormConfirm)} tabIndex={-1}><EyeIcon visible={showFormConfirm} /></button>
                  </div>
                </div>
              </div>
              <div className="admin-form-row">
                <div className="admin-form-field">
                  <label>Role</label>
                  <select name="role" value={form.role} onChange={handleFormChange}>
                    {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                  </select>
                </div>
                <div className="admin-form-field">
                  <label>Session</label>
                  <select name="session" value={form.session} onChange={handleFormChange}>
                    {SESSIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="admin-form-field">
                  <label>Department</label>
                  <select name="department" value={form.department} onChange={handleFormChange}>
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <button type="submit" className="admin-submit-btn" disabled={formLoading}>
                {formLoading ? 'Creating...' : 'Create User'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showProfile && (
        <div className="admin-modal-overlay" onClick={() => setShowProfile(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Edit My Profile</h2>
              <button type="button" className="admin-modal-close" onClick={() => setShowProfile(false)}>×</button>
            </div>
            <div className="admin-profile-info">
              <div className="admin-avatar-lg">{(currentUser.full_name || currentUser.username || 'A')[0].toUpperCase()}</div>
              <div>
                <div className="admin-profile-username">@{currentUser.username}</div>
                <div className="admin-profile-role">{currentUser.role}</div>
              </div>
            </div>
            {profileError && <div className="admin-form-error">{profileError}</div>}
            {profileSuccess && <div className="admin-form-success">{profileSuccess}</div>}
            <form onSubmit={handleUpdateProfile} className="admin-form">
              <div className="admin-form-row">
                <div className="admin-form-field">
                  <label>Full Name</label>
                  <input name="full_name" value={profileForm.full_name} onChange={handleProfileChange} placeholder="Your full name" required />
                </div>
                <div className="admin-form-field">
                  <label>Email</label>
                  <input type="email" name="email" value={profileForm.email} onChange={handleProfileChange} placeholder="your@email.com" required />
                </div>
              </div>
              <div className="admin-form-row">
                <div className="admin-form-field">
                  <label>Phone Number</label>
                  <input name="phone_number" value={profileForm.phone_number} onChange={handleProfileChange} placeholder="+2348012345678" />
                </div>
                <div className="admin-form-field">
                  <label>Session</label>
                  <select name="session" value={profileForm.session} onChange={handleProfileChange}>
                    <option value="">-- Select --</option>
                    {SESSIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="admin-form-field">
                  <label>Department</label>
                  <select name="department" value={profileForm.department} onChange={handleProfileChange}>
                    <option value="">-- Select --</option>
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <div className="admin-form-divider">Change Password <span>(leave blank to keep current)</span></div>
              <div className="admin-form-row">
                <div className="admin-form-field">
                  <label>New Password</label>
                  <div className="admin-input-wrap">
                    <input type={showProfileNewPwd ? 'text' : 'password'} name="new_password" value={profileForm.new_password} onChange={handleProfileChange} placeholder="New password" />
                    <button type="button" className="admin-eye-btn" onClick={() => setShowProfileNewPwd(!showProfileNewPwd)} tabIndex={-1}><EyeIcon visible={showProfileNewPwd} /></button>
                  </div>
                </div>
                <div className="admin-form-field">
                  <label>Confirm New Password</label>
                  <div className="admin-input-wrap">
                    <input type={showProfileConfirm ? 'text' : 'password'} name="confirm_password" value={profileForm.confirm_password} onChange={handleProfileChange} placeholder="Repeat new password" />
                    <button type="button" className="admin-eye-btn" onClick={() => setShowProfileConfirm(!showProfileConfirm)} tabIndex={-1}><EyeIcon visible={showProfileConfirm} /></button>
                  </div>
                </div>
              </div>
              <button type="submit" className="admin-submit-btn" disabled={profileLoading}>
                {profileLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetTarget && (
        <div className="admin-modal-overlay" onClick={() => setResetTarget(null)}>
          <div className="admin-modal admin-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Reset Password</h2>
              <button type="button" className="admin-modal-close" onClick={() => setResetTarget(null)}>×</button>
            </div>
            <p className="admin-reset-for">
              Setting new password for <strong>{resetTarget.full_name}</strong> (@{resetTarget.username})
            </p>
            {resetError && <div className="admin-form-error">{resetError}</div>}
            {resetSuccess && <div className="admin-form-success">{resetSuccess}</div>}
            <form onSubmit={handleResetPassword} className="admin-form">
              <div className="admin-form-field">
                <label>New Password</label>
                <div className="admin-input-wrap">
                  <input
                    type={showResetPwd ? 'text' : 'password'}
                    value={resetForm.new_password}
                    onChange={(e) => setResetForm({ ...resetForm, new_password: e.target.value })}
                    placeholder="Enter new password"
                    required
                  />
                  <button type="button" className="admin-eye-btn" onClick={() => setShowResetPwd(!showResetPwd)} tabIndex={-1}>
                    <EyeIcon visible={showResetPwd} />
                  </button>
                </div>
              </div>
              <div className="admin-form-field">
                <label>Confirm New Password</label>
                <div className="admin-input-wrap">
                  <input
                    type={showResetConfirm ? 'text' : 'password'}
                    value={resetForm.confirm_password}
                    onChange={(e) => setResetForm({ ...resetForm, confirm_password: e.target.value })}
                    placeholder="Confirm new password"
                    required
                  />
                  <button type="button" className="admin-eye-btn" onClick={() => setShowResetConfirm(!showResetConfirm)} tabIndex={-1}>
                    <EyeIcon visible={showResetConfirm} />
                  </button>
                </div>
              </div>
              <button type="submit" className="admin-submit-btn" disabled={resetLoading}>
                {resetLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editTarget && (
        <div className="admin-modal-overlay" onClick={() => setEditTarget(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Edit User</h2>
              <button type="button" className="admin-modal-close" onClick={() => setEditTarget(null)}>×</button>
            </div>
            <div className="admin-profile-info">
              <div className="admin-avatar-lg">{(editTarget.full_name || editTarget.username || 'U')[0].toUpperCase()}</div>
              <div>
                <div className="admin-profile-username">@{editTarget.username}</div>
                <div className="admin-profile-role">{editTarget.role}</div>
              </div>
            </div>
            {editError && <div className="admin-form-error">{editError}</div>}
            {editSuccess && <div className="admin-form-success">{editSuccess}</div>}
            <form onSubmit={handleEditUser} className="admin-form">
              <div className="admin-form-row">
                <div className="admin-form-field">
                  <label>Full Name</label>
                  <input name="full_name" value={editForm.full_name} onChange={(e) => setEditForm({...editForm, full_name: e.target.value})} placeholder="Full name" required />
                </div>
                <div className="admin-form-field">
                  <label>Email</label>
                  <input type="email" name="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} placeholder="email@example.com" required />
                </div>
              </div>
              <div className="admin-form-row">
                <div className="admin-form-field">
                  <label>Phone Number</label>
                  <input name="phone_number" value={editForm.phone_number} onChange={(e) => setEditForm({...editForm, phone_number: e.target.value})} placeholder="+2348012345678" />
                </div>
                <div className="admin-form-field">
                  <label>Role</label>
                  <select name="role" value={editForm.role} onChange={(e) => setEditForm({...editForm, role: e.target.value})} disabled={editTarget.id === currentUser.id}>
                    {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                  </select>
                  {editTarget.id === currentUser.id && <small style={{color:'#888'}}>Cannot change your own role</small>}
                </div>
              </div>
              <div className="admin-form-row">
                <div className="admin-form-field">
                  <label>Session</label>
                  <select name="session" value={editForm.session} onChange={(e) => setEditForm({...editForm, session: e.target.value})}>
                    <option value="">-- Select --</option>
                    {SESSIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="admin-form-field">
                  <label>Department</label>
                  <select name="department" value={editForm.department} onChange={(e) => setEditForm({...editForm, department: e.target.value})}>
                    <option value="">-- Select --</option>
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <button type="submit" className="admin-submit-btn" disabled={editLoading}>
                {editLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="admin-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="admin-modal admin-modal-sm" onClick={(e) => e.stopPropagation()}>
            <h2 className="admin-confirm-title">Delete User</h2>
            <p className="admin-confirm-text">
              Are you sure you want to permanently delete <strong>{deleteConfirm.full_name}</strong> (@{deleteConfirm.username})? This cannot be undone.
            </p>
            <div className="admin-confirm-actions">
              <button type="button" className="admin-cancel-btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button type="button" className="admin-delete-confirm-btn" onClick={() => handleDelete(deleteConfirm.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
